import React from 'react'
import { ActivityIndicator, View, StyleSheet, TextInput, Pressable, ScrollView } from 'react-native'
import { fetchAvailability, StateroomTypeGrouping } from '../networking/fetchAvailability'
import { countAvailableRooms, formatDateRange, formatItinerary, getSailingById, getShipLogo, getTotalRooms, extractRoomNumbers } from '../utils/helpers'
import sailingsData from '../data/sailings.json'
import { CommonActions, RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import Header, { DCL_BLUE } from '../components/Header'
import { Typography } from '../components/Typography'
import { Card } from '../components/Card'
import { UpcomingSailings } from '../components/UpcomingSailings'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
// Deep-import individual icons instead of the package barrel file. Expo's webpack
// config disables tree-shaking (`usedExports: false`) for React Native compatibility,
// so importing from the barrel (`@fortawesome/free-solid-svg-icons`) bundles all ~1400
// icons (~1MB+ minified) instead of just the ones used here.
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight'
import { SvgUri } from 'react-native-svg'

export type HomeScreenRouteParams = {
  sailing?: string
}

const styles = StyleSheet.create({
  page: {
    alignItems: 'center',
  },
  container: {
    width: '100%',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 64,
    maxWidth: 1200,
    gap: 16,
  },
  title: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  inputContainer: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
    width: '100%',
  },
  logo: {
    alignItems: 'center',
    gap: 8,
    flexDirection: 'row',
  },
  titleButtons: {
    flexDirection: 'row',
    gap: 16,
  },
  arrowButton: {
    backgroundColor: '#E9E9E9',
    height: 34,
    width: 34,
    borderRadius: 34,
    justifyContent: 'center',
    alignItems: 'center',
  },
  roomNumber: {
    backgroundColor: '#F0F0F0',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  roomNumberList: {
    marginTop: 4,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  submitButton: {
    backgroundColor: DCL_BLUE,
    padding: 8,
    borderRadius: 4
  },
  submitButtonDisabled: {
    backgroundColor: '#E9E9E9',
  }
})

const HomeScreen = () => {
  const navigation = useNavigation()
  const route = useRoute<RouteProp<{ Home: HomeScreenRouteParams }, 'Home'>>()
  const sailing = route.params?.sailing
  const sailingNumber = parseInt(sailing?.slice(2) || '0', 10)
  const ship = sailing?.slice(0, 2)

  const [loading, setLoading] = React.useState(true)
  const [stateroomTypes, setStateroomTypes] = React.useState<StateroomTypeGrouping | undefined>(undefined)
  const [dateRange, setDateRange] = React.useState<string | undefined>(undefined)
  const [sailingInput, setSailingInput] = React.useState<string>('')
  const submitButtonDisabled = sailingInput.length !== 6

  if (!sailing || sailing === undefined) {
    return (
      <ScrollView contentContainerStyle={styles.page}>
        <Header />
        <View style={styles.container}>
          <Typography variant='title'>Enter a sailing ID</Typography>
          <Typography variant='body'>
            This value can be found in the Disney Cruise Website URL for a sailing.{`\n`}
            It is two letters followed by 4 numbers. Ex. (WW0372).{`\n`}
            https://disneycruise.disney.go.com/cruises-destinations/list/WW0372</Typography>
          <View style={styles.inputContainer}>
            <TextInput
              style={{
                borderWidth: 1,
                borderColor: DCL_BLUE,
                borderRadius: 4,
                padding: 8,
                width: 100,
                textTransform: 'uppercase'
              }}
              placeholder="XX0000"
              maxLength={6}
              autoCapitalize="characters"
              onChangeText={(text) => {
                const filtered = text.replace(/[^A-Za-z0-9]/g, '')
                const letters = filtered.slice(0, 2).replace(/[^A-Za-z]/g, '')
                const numbers = filtered.slice(2, 6).replace(/[^0-9]/g, '')
                setSailingInput(letters + numbers)
              }}
              value={sailingInput}
            />
            <Pressable
              style={[styles.submitButton,
                      submitButtonDisabled && styles.submitButtonDisabled]}
              disabled={submitButtonDisabled}
              onPress={() => {
                const sailingRegex = /^[A-Za-z]{2}\d{4}$/
                if (sailingRegex.test(sailingInput)) {
                  navigation.dispatch(
                    CommonActions.navigate({
                      name: 'Sailing',
                      params: { sailing: sailingInput.toUpperCase() }
                    })
                  )
                }
              }}>
              <Typography variant="body" color="#FFFFFF">Submit</Typography>
            </Pressable>
          </View>
        </View>
        <View style={styles.container}>
          <UpcomingSailings />
        </View>
      </ScrollView>
    )
  }

  React.useEffect(() => {
    const fetch = async () => {
      setLoading(true)
      try {
        const availability = await fetchAvailability(sailing)
        setStateroomTypes(availability?.sessions[0].stateroomTypes)
        
        const voyagePackage = availability?.sessions[0].voyagePackage
        setDateRange(formatDateRange(voyagePackage?.vacStartDate, voyagePackage?.vacEndDate))

        setLoading(false)
      } catch (error) {
        console.error("Error fetching sailings:", error)
        setLoading(false)
      }
    }
    fetch()
  }, [sailing])

  const sailingDetails = getSailingById(sailingsData.sailings, sailing)
  const itinerary = sailingDetails && formatItinerary(sailingDetails)

  const totalRooms = getTotalRooms(sailing)
  const availableRoomCount = countAvailableRooms(stateroomTypes)
  const availableRoomPercentage = totalRooms > 0 ? Math.ceil((availableRoomCount / totalRooms) * 100) : 0

  console.log(stateroomTypes)

  return (
    <View style={styles.page}>
      <Header />
      <View style={styles.container}>
        <View>
          <View style={styles.title}>
            <View style={styles.logo}>
              <Typography variant='title'>{sailing}</Typography>
              <SvgUri 
                uri={getShipLogo(sailing)}
                fill={DCL_BLUE}
                height={34}
                width={80}
              />
            </View>
            {!loading && (
              <View style={styles.titleButtons}>
                  <Pressable style={styles.arrowButton} onPress={() => {
                    navigation.dispatch(
                      CommonActions.navigate({
                        name: 'Sailing',
                        params: { sailing: `${ship}${String(sailingNumber - 1).padStart(4, '0')}` },
                      })
                    )
                  }}>
                    <FontAwesomeIcon icon={faArrowLeft} size={16} color={DCL_BLUE} />
                  </Pressable>

                  <Pressable style={styles.arrowButton} onPress={() => {
                    navigation.dispatch(
                      CommonActions.navigate({
                        name: 'Sailing',
                        params: { sailing: `${ship}${String(sailingNumber + 1).padStart(4, '0')}` },
                      })
                    )
                  }}>
                    <FontAwesomeIcon icon={faArrowRight} size={16} color={DCL_BLUE} />
                  </Pressable>
              </View>
            )}
          </View>
          {dateRange && !loading && <Typography variant='subheading'>{dateRange}</Typography>}
          {itinerary && !loading && <Typography variant='body' color="#6E6E6E">{itinerary}</Typography>}
        </View>
        {loading && <ActivityIndicator size="large" color={DCL_BLUE} style={{ marginVertical: 32 }} />}
        {!loading && stateroomTypes && (
          <>
            <Card style={{ marginBottom: 32 }}>
              <Typography variant='heading'>{availableRoomCount} available rooms ({availableRoomPercentage}%)</Typography>
              <Typography variant='subheading'>{totalRooms.toLocaleString()} total rooms</Typography>
            </Card>
            {Object.entries(stateroomTypes).map(([key, value]) => (
              <Card key={key}>
                <Typography variant='heading'>{key}</Typography>
                <Typography variant='subheading' style={{ marginBottom: 8 }}>{`Available Stateroom Count: ${value.stateroomCount}`}</Typography>
                {value.stateroomSubtypes && Object.entries(value.stateroomSubtypes).map(([subtypeKey, subtypeValue]) => {
                  const roomNumbers = extractRoomNumbers(subtypeValue)
                  return (
                    <View key={subtypeKey} style={{ marginBottom: 12 }}>
                      <Typography variant='body' style={{ marginBottom: 4 }}>{subtypeKey} - {subtypeValue.stateroomCount}</Typography>
                      {roomNumbers.length > 0 && (
                        <View style={styles.roomNumberList}>
                          {roomNumbers.sort((a, b) => Number(b) - Number(a)).map((roomNumber) => (
                            <View key={roomNumber} style={styles.roomNumber}>
                              <Typography variant='body' style={{ fontSize: 12 }}>{roomNumber}</Typography>
                            </View>
                          ))}
                        </View>
                      )}
                    </View>
                  )
                })}
              </Card>
            ))}
            
          </>
        )}
      </View>
    </View>
  )
}

export default HomeScreen